/**
 * Onboarding Store - Zustand state management with localStorage persistence
 *
 * Manages the complete onboarding flow state including:
 * - Role selection and progress tracking
 * - Legal signature data
 * - Quiz results and attempts
 * - Soft/hard gate completion status
 *
 * MVP Note: Currently localStorage-only. Backend sync is stubbed for future.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type {
  OnboardingStore,
  OnboardingRole,
  ModuleId,
  SignatureData,
  QuizAttempt,
  PolicyType,
  QuizType,
} from '../types/onboarding';
import {
  STORAGE_KEYS,
  CURRENT_ONBOARDING_VERSION,
  QUIZ_CONFIG,
} from '../lib/onboarding/constants';
import {
  getStepsForRole,
  areHardGateStepsComplete,
  areAllStepsComplete,
} from '../lib/onboarding/steps';

// ============================================================================
// Default State
// ============================================================================

const defaultState = {
  // Versioning & Role
  onboardingVersion: CURRENT_ONBOARDING_VERSION,
  role: null as OnboardingRole | null,
  hasSyncedToServer: false,

  // Progress
  currentModule: 0,
  completedModules: [] as ModuleId[],
  isComplete: false,
  completedAt: null as string | null,
  certificateId: null as string | null,

  // Legal signatures
  ndaSignature: null as SignatureData | null,
  ipAssignmentSignature: null as SignatureData | null,
  tosAcceptedAt: null as string | null,

  // Quiz results
  securityQuiz: {
    score: null as number | null,
    attemptsCount: 0,
    lastAttemptAt: null as string | null,
    passed: false,
  },
  systemQuiz: {
    score: null as number | null,
    attemptsCount: 0,
    lastAttemptAt: null as string | null,
    passed: false,
  },

  // Profile data
  profileData: {
    displayName: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  },

  // Soft gate tracking
  dashboardTourCompleted: false,
  systemEducationCompleted: false,
};

// ============================================================================
// Store Creation
// ============================================================================

export const useOnboardingStore = create<OnboardingStore>()(
  persist(
    (set, get) => ({
      ...defaultState,

      // ──────────────────────────────────────────────────────────────────────
      // Role & Profile Actions
      // ──────────────────────────────────────────────────────────────────────

      setRole: (role: OnboardingRole) => {
        set({ role });
      },

      setDisplayName: (name: string) => {
        set((state) => ({
          profileData: { ...state.profileData, displayName: name },
        }));
      },

      // ──────────────────────────────────────────────────────────────────────
      // Navigation Actions
      // ──────────────────────────────────────────────────────────────────────

      nextModule: () => {
        const { role, currentModule } = get();
        if (!role) return;

        const steps = getStepsForRole(role);
        if (currentModule < steps.length - 1) {
          set({ currentModule: currentModule + 1 });
        }
      },

      prevModule: () => {
        const { currentModule } = get();
        if (currentModule > 0) {
          set({ currentModule: currentModule - 1 });
        }
      },

      goToModule: (index: number) => {
        const { role, completedModules } = get();
        if (!role) return;

        const steps = getStepsForRole(role);
        // Can only navigate to completed modules or the next one
        if (index >= 0 && index < steps.length) {
          const targetStep = steps[index];
          const isAccessible =
            completedModules.includes(targetStep.id) ||
            index === completedModules.length;

          if (isAccessible) {
            set({ currentModule: index });
          }
        }
      },

      // ──────────────────────────────────────────────────────────────────────
      // Module Completion
      // ──────────────────────────────────────────────────────────────────────

      completeModule: (moduleId: ModuleId) => {
        const { completedModules, role } = get();
        if (!role) return;

        if (!completedModules.includes(moduleId)) {
          const newCompleted = [...completedModules, moduleId];

          // Check if all steps are now complete
          const allComplete = areAllStepsComplete(newCompleted, role);

          set({
            completedModules: newCompleted,
            isComplete: allComplete,
            completedAt: allComplete ? new Date().toISOString() : null,
          });
        }
      },

      // ──────────────────────────────────────────────────────────────────────
      // Legal Actions
      // ──────────────────────────────────────────────────────────────────────

      signPolicy: (type: PolicyType, signature: SignatureData) => {
        switch (type) {
          case 'nda':
            set({ ndaSignature: signature });
            break;
          case 'ip_assignment':
            set({ ipAssignmentSignature: signature });
            break;
          case 'tos':
            set({ tosAcceptedAt: signature.signedAt });
            break;
        }
      },

      // ──────────────────────────────────────────────────────────────────────
      // Quiz Actions
      // ──────────────────────────────────────────────────────────────────────

      submitQuiz: (type: QuizType, result: QuizAttempt) => {
        const config = QUIZ_CONFIG[type];
        const passed = result.percentage >= config.passThreshold * 100;

        if (type === 'security') {
          set((state) => ({
            securityQuiz: {
              score: result.percentage,
              attemptsCount: state.securityQuiz.attemptsCount + 1,
              lastAttemptAt: result.attemptedAt,
              passed,
            },
          }));
        } else {
          set((state) => ({
            systemQuiz: {
              score: result.percentage,
              attemptsCount: state.systemQuiz.attemptsCount + 1,
              lastAttemptAt: result.attemptedAt,
              passed,
            },
          }));
        }
      },

      // ──────────────────────────────────────────────────────────────────────
      // Soft Gate Actions
      // ──────────────────────────────────────────────────────────────────────

      completeDashboardTour: () => {
        set({ dashboardTourCompleted: true });
      },

      completeSystemEducation: () => {
        set({ systemEducationCompleted: true });
      },

      // ──────────────────────────────────────────────────────────────────────
      // Completion & Sync Actions
      // ──────────────────────────────────────────────────────────────────────

      markComplete: (certificateId: string) => {
        set({
          isComplete: true,
          completedAt: new Date().toISOString(),
          certificateId,
        });
      },

      markSynced: () => {
        set({ hasSyncedToServer: true });
      },

      reset: () => {
        set(defaultState);
      },
    }),
    {
      name: STORAGE_KEYS.onboardingStore,
      storage: createJSONStorage(() => localStorage),
      version: 1,
      // Only persist user data, not functions
      partialize: (state) => ({
        onboardingVersion: state.onboardingVersion,
        role: state.role,
        hasSyncedToServer: state.hasSyncedToServer,
        currentModule: state.currentModule,
        completedModules: state.completedModules,
        isComplete: state.isComplete,
        completedAt: state.completedAt,
        certificateId: state.certificateId,
        ndaSignature: state.ndaSignature,
        ipAssignmentSignature: state.ipAssignmentSignature,
        tosAcceptedAt: state.tosAcceptedAt,
        securityQuiz: state.securityQuiz,
        systemQuiz: state.systemQuiz,
        profileData: state.profileData,
        dashboardTourCompleted: state.dashboardTourCompleted,
        systemEducationCompleted: state.systemEducationCompleted,
      }),
    }
  )
);

// ============================================================================
// Selector Hooks for Performance
// ============================================================================

export const useOnboardingRole = () =>
  useOnboardingStore((state) => state.role);

export const useOnboardingProgress = () =>
  useOnboardingStore((state) => ({
    currentModule: state.currentModule,
    completedModules: state.completedModules,
    isComplete: state.isComplete,
  }));

export const useIsOnboardingComplete = () =>
  useOnboardingStore((state) => state.isComplete);

export const useOnboardingVersion = () =>
  useOnboardingStore((state) => state.onboardingVersion);

/**
 * Check if hard gate (modules 1-3) is complete
 */
export const useIsHardGateComplete = () =>
  useOnboardingStore((state) => {
    if (!state.role) return false;
    return areHardGateStepsComplete(state.role, state.completedModules);
  });

/**
 * Get certificate info
 */
export const useCertificate = () =>
  useOnboardingStore((state) => ({
    certificateId: state.certificateId,
    completedAt: state.completedAt,
    role: state.role,
    displayName: state.profileData.displayName,
  }));

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Generate a ULID-style certificate ID
 * Format: -ONB-YYYY-XXXXXX
 */
export function generateCertificateId(): string {
  const year = new Date().getFullYear();
  const chars = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
  let suffix = '';
  for (let i = 0; i < 6; i++) {
    suffix += chars[Math.floor(Math.random() * chars.length)];
  }
  return `Λ-ONB-${year}-${suffix}`;
}

/**
 * Generate SHA-256 hash for signature verification
 */
export async function generateSignatureHash(
  typedName: string,
  timestamp: string
): Promise<string> {
  const data = `${typedName}|${timestamp}`;
  const encoder = new TextEncoder();
  const dataBuffer = encoder.encode(data);
  const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Check if user needs to re-onboard due to version mismatch
 */
export function needsVersionUpdate(
  storedVersion: string,
  currentVersion: string
): boolean {
  return storedVersion !== currentVersion;
}
